import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  StaffTimecard,
  StaffTimecardStatus,
  StaffTimecardDailyView,
  ClockOperationResult,
  GetStaffTimecardParams,
  GetDailyViewParams,
  SelfClockInRequest,
  SelfClockOutRequest,
  InsertStaffTimecardParams,
  UpdateStaffTimecardParams,
  ClockInParams,
  ClockOutParams
} from '../models/staff-timecard.model';

@Injectable({
  providedIn: 'root'
})
export class StaffTimecardService {
  private API_URL = environment.API_URL + 'StaffTimecard/';

  constructor(private http: HttpClient) { }

  // ============ Admin Endpoints ============

  /**
   * Get timecards with filters and pagination (Admin only)
   */
  getTimecards(params: GetStaffTimecardParams): Observable<StaffTimecard[]> {
    const queryParams = this.buildQueryString(params);
    return this.http.get<StaffTimecard[]>(this.API_URL + 'GetTimecards' + queryParams)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to load timecards')))
      );
  }

  /**
   * Get daily view showing all staff with schedules and timecards (Admin only)
   */
  getDailyView(params: GetDailyViewParams): Observable<StaffTimecardDailyView[]> {
    const queryParams = this.buildQueryString(params);
    return this.http.get<StaffTimecardDailyView[]>(this.API_URL + 'GetDailyView' + queryParams)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to load daily view')))
      );
  }

  /**
   * Create a manual timecard entry (Admin only)
   */
  createTimecard(params: InsertStaffTimecardParams): Observable<ClockOperationResult> {
    return this.http.post<ClockOperationResult>(this.API_URL + 'CreateTimecard', params)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to create timecard')))
      );
  }

  /**
   * Update an existing timecard (Admin only)
   * Using POST instead of PUT to avoid CORS preflight issues
   */
  updateTimecard(params: UpdateStaffTimecardParams): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(this.API_URL + 'UpdateTimecard', params)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to update timecard')))
      );
  }

  /**
   * Delete a timecard (Admin only, soft delete)
   * Using POST instead of DELETE to avoid CORS preflight issues
   */
  deleteTimecard(timecardId: number): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(this.API_URL + 'DeleteTimecard', { timecardID: timecardId })
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to delete timecard')))
      );
  }

  /**
   * Admin clock-in for a specific user
   */
  adminClockIn(params: ClockInParams): Observable<ClockOperationResult> {
    return this.http.post<ClockOperationResult>(this.API_URL + 'AdminClockIn', params)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to clock in user')))
      );
  }

  /**
   * Admin clock-out for a specific user
   */
  adminClockOut(params: ClockOutParams): Observable<ClockOperationResult> {
    return this.http.post<ClockOperationResult>(this.API_URL + 'AdminClockOut', params)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to clock out user')))
      );
  }

  // ============ Staff Self-Service Endpoints ============

  /**
   * Get today's clock status for the current user
   */
  getMyTodayStatus(branchId: number): Observable<StaffTimecardStatus> {
    return this.http.get<StaffTimecardStatus>(this.API_URL + `GetMyTodayStatus?branchId=${branchId}`)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to load status')))
      );
  }

  /**
   * Get timecard history for the current user
   */
  getMyTimecards(branchId?: number, fromDate?: string, toDate?: string, offSetRows: number = 0, nextRows: number = 50): Observable<StaffTimecard[]> {
    let params: any = { offSetRows, nextRows };
    if (branchId) params.branchID = branchId;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;

    const queryParams = this.buildQueryString(params);
    return this.http.get<StaffTimecard[]>(this.API_URL + 'GetMyTimecards' + queryParams)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to load timecards')))
      );
  }

  /**
   * Clock in for the current user
   */
  clockIn(request: SelfClockInRequest): Observable<ClockOperationResult> {
    return this.http.post<ClockOperationResult>(this.API_URL + 'ClockIn', request)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to clock in')))
      );
  }

  /**
   * Clock out for the current user
   */
  clockOut(request: SelfClockOutRequest): Observable<ClockOperationResult> {
    return this.http.post<ClockOperationResult>(this.API_URL + 'ClockOut', request)
      .pipe(
        map((data: any) => data),
        catchError(error => throwError(() => new Error('Failed to clock out')))
      );
  }

  /**
   * Helper to build query string from params object
   */
  private buildQueryString(params: any): string {
    const queryParts: string[] = [];
    for (const key in params) {
      if (params[key] !== null && params[key] !== undefined) {
        let value = params[key];
        // Format date values
        if (value instanceof Date) {
          value = value.toISOString().split('T')[0];
        }
        queryParts.push(`${key}=${encodeURIComponent(value)}`);
      }
    }
    return queryParts.length > 0 ? '?' + queryParts.join('&') : '';
  }
}
